import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  UserPlus,
  AlertCircle,
  Heart,
  Sparkles,
  User,
  Mail,
  Phone,
  Shield,
} from "lucide-react";
import { type CreateStudentRequest } from "@/services";

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (studentData: CreateStudentRequest) => Promise<void>;
}

export const AddChildModal: React.FC<AddChildModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<CreateStudentRequest>({
    nombre: "",
    apellido: "",
    rut: "",
    email: "",
    telefono: "",
    password: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /**
   * Valida formato de RUT chileno
   */
  const validateRut = (rut: string): boolean => {
    // Limpiar RUT de puntos y guión
    const cleanRut = rut.replace(/[.-]/g, '');
    
    // Verificar longitud mínima
    if (cleanRut.length < 8) return false;
    
    // Verificar que solo contenga números y una letra al final
    if (!/^[0-9]+[0-9kK]$/.test(cleanRut)) return false;
    
    // Verificar dígito verificador
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toUpperCase();
    
    let sum = 0;
    let multiplier = 2;
    
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDv = 11 - (sum % 11);
    const calculatedDv = expectedDv === 11 ? '0' : expectedDv === 10 ? 'K' : expectedDv.toString();
    
    return dv === calculatedDv;
  };

  /**
   * Valida formato de email
   */
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email es opcional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Valida formato de teléfono chileno
   */
  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Teléfono es opcional
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 8 && cleanPhone.length <= 9;
  };

  /**
   * Maneja el cambio en los campos del formulario
   */
  const handleInputChange = (
    field: keyof CreateStudentRequest,
    value: string
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    
    // Limpiar errores específicos del campo
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    setError(null); // Limpiar error general al cambiar
  };

  /**
   * Valida el formulario antes de enviar
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validar nombre
    if (!formData.nombre.trim()) {
      errors.nombre = "El nombre es requerido";
    } else if (formData.nombre.trim().length < 2) {
      errors.nombre = "El nombre debe tener al menos 2 caracteres";
    }

    // Validar apellido
    if (!formData.apellido.trim()) {
      errors.apellido = "El apellido es requerido";
    } else if (formData.apellido.trim().length < 2) {
      errors.apellido = "El apellido debe tener al menos 2 caracteres";
    }

    // Validar RUT
    if (!formData.rut.trim()) {
      errors.rut = "El RUT es requerido";
    } else if (!validateRut(formData.rut)) {
      errors.rut = "El RUT ingresado no es válido";
    }

    // Validar email (si se proporciona)
    if (formData.email && !validateEmail(formData.email)) {
      errors.email = "El formato del email no es válido";
    }

    // Validar teléfono (si se proporciona)
    if (formData.telefono && !validatePhone(formData.telefono)) {
      errors.telefono = "El teléfono debe tener entre 8 y 9 dígitos";
    }

    // Validar contraseña
    if (!formData.password.trim()) {
      errors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      errors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Procesa el mensaje de error del servidor para hacerlo más descriptivo
   */
  const processServerError = (error: any): string => {
    // Si es un error de respuesta del servidor
    if (error.response?.data?.message) {
      const serverMessage = error.response.data.message;
      
      // Mapear mensajes específicos del servidor
      if (serverMessage.includes('Ya existe un usuario con ese RUT')) {
        return "Ya existe un estudiante registrado con ese RUT. Verifica que no esté duplicado.";
      }
      
      if (serverMessage.includes('Ya existe un usuario con ese email')) {
        return "Ya existe un estudiante registrado con ese email. Intenta con otro email o déjalo vacío para que se genere automáticamente.";
      }
      
      return serverMessage;
    }
    
    // Si es un error de red
    if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
      return "Error de conexión. Verifica tu conexión a internet e intenta nuevamente.";
    }
    
    // Si es un error 400 (Bad Request)
    if (error.response?.status === 400) {
      return "Los datos ingresados no son válidos. Revisa la información e intenta nuevamente.";
    }
    
    // Si es un error 500 (Server Error)
    if (error.response?.status === 500) {
      return "Error interno del servidor. Intenta nuevamente en unos momentos.";
    }
    
    // Mensaje por defecto
    return error.message || "Error al registrar estudiante. Intenta nuevamente.";
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      // Limpiar formulario después del éxito
      setFormData({
        nombre: "",
        apellido: "",
        rut: "",
        email: "",
        telefono: "",
        password: "",
      });
      setFieldErrors({});
      onClose();
    } catch (err: any) {
      const processedError = processServerError(err);
      setError(processedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        nombre: "",
        apellido: "",
        rut: "",
        email: "",
        telefono: "",
        password: "",
      });
      setError(null);
      setFieldErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-gradient-to-br from-card/90 to-card/70 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-2xl">
        <DialogHeader className="pb-4 sm:pb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                Registrar Nuevo Estudiante
              </DialogTitle>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Agrega a tu hijo al sistema para gestionar sus productos
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Mensaje de error mejorado */}
          {error && (
            <Alert
              variant="destructive"
              className="border-red-500/20 bg-red-500/10 rounded-xl sm:rounded-2xl"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {/* Información del estudiante */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Heart className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Información Personal
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Completa los datos de tu hijo para registrarlo en el sistema
            </p>
          </div>

          {/* Nombre y Apellido en fila - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="nombre"
                className="text-sm font-medium flex items-center gap-2"
              >
                <User className="h-4 w-4 text-muted-foreground" />
                Nombre *
              </Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                placeholder="Ingresa el nombre"
                disabled={isSubmitting}
                className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                  fieldErrors.nombre ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                }`}
                required
              />
              {fieldErrors.nombre && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {fieldErrors.nombre}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="apellido"
                className="text-sm font-medium flex items-center gap-2"
              >
                <User className="h-4 w-4 text-muted-foreground" />
                Apellido *
              </Label>
              <Input
                id="apellido"
                value={formData.apellido}
                onChange={(e) => handleInputChange("apellido", e.target.value)}
                placeholder="Ingresa el apellido"
                disabled={isSubmitting}
                className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                  fieldErrors.apellido ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                }`}
                required
              />
              {fieldErrors.apellido && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {fieldErrors.apellido}
                </p>
              )}
            </div>
          </div>

          {/* RUT */}
          <div className="space-y-2">
            <Label
              htmlFor="rut"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Shield className="h-4 w-4 text-muted-foreground" />
              RUT *
            </Label>
            <Input
              id="rut"
              value={formData.rut}
              onChange={(e) => handleInputChange("rut", e.target.value)}
              placeholder="12345678-9"
              disabled={isSubmitting}
              className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                fieldErrors.rut ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
              }`}
              required
            />
            {fieldErrors.rut && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {fieldErrors.rut}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Mail className="h-4 w-4 text-muted-foreground" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="estudiante@email.com"
              disabled={isSubmitting}
              className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
              }`}
            />
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Si no se proporciona, se generará automáticamente
            </p>
            {fieldErrors.email && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {fieldErrors.email}
              </p>
            )}
          </div>

          {/* Teléfono */}
          <div className="space-y-2">
            <Label
              htmlFor="telefono"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Phone className="h-4 w-4 text-muted-foreground" />
              Teléfono
            </Label>
            <Input
              id="telefono"
              value={formData.telefono}
              onChange={(e) => handleInputChange("telefono", e.target.value)}
              placeholder="912345678"
              disabled={isSubmitting}
              className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                fieldErrors.telefono ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
              }`}
            />
            {fieldErrors.telefono && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {fieldErrors.telefono}
              </p>
            )}
          </div>

          {/* Contraseña */}
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-sm font-medium flex items-center gap-2"
            >
              <Shield className="h-4 w-4 text-muted-foreground" />
              Contraseña *
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder="Mínimo 6 caracteres"
              disabled={isSubmitting}
              className={`h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm ${
                fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
              }`}
              required
            />
            {fieldErrors.password && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Footer con botones mejorados - Mobile Optimized */}
          <DialogFooter className="pt-4 sm:pt-6">
            <div className="flex gap-2 sm:gap-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 h-10 sm:h-12 hover:bg-primary/10 transition-all duration-200 rounded-xl sm:rounded-2xl text-sm"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 h-10 sm:h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl sm:rounded-2xl text-sm"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                <UserPlus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Registrar Estudiante</span>
                <span className="sm:hidden">Registrar</span>
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
